import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Autocomplete
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { corregirDominioTipeado } from '../utils/emailDominios';
import {
  PAISES_FRECUENTES,
  TODOS_LOS_PAISES,
  banderaDePais,
  detectarPaisDelVisitante,
  paisCalzaConBusqueda,
  paisPorId,
  prefijoDePais,
  separarPrefijo,
} from '../utils/codigosPais';

// Opciones del selector: los frecuentes arriba y después todos de la A a la Z.
// Los frecuentes van repetidos en los dos grupos, así que cada opción lleva su
// grupo para distinguirlas.
const OPCIONES_PAIS = [
  ...PAISES_FRECUENTES.map((p) => ({ ...p, grupo: 'Más frecuentes' })),
  ...TODOS_LOS_PAISES.map((p) => ({ ...p, grupo: 'Todos los países (A-Z)' })),
];

const textoDePais = (pais) => `${banderaDePais(pais.id)} ${pais.code}`;

// Sin búsqueda se muestran los dos grupos; al buscar, solo el listado
// completo (si no, Bolivia saldría dos veces).
const filtrarPaises = (opciones, { inputValue }) => {
  if (!inputValue.trim()) return opciones;
  return opciones.filter(
    (p) => p.grupo !== 'Más frecuentes' && paisCalzaConBusqueda(p, inputValue)
  );
};

const VideoAccessForm = ({
  open,
  onClose,
  onSubmit,
  title = 'Desbloquea el Video',
  subtitle = 'Completa tus datos para ver el video completo',
  submitLabel = 'Ver Video Ahora',
}) => {
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    instagram: '',
    whatsapp: '',
    pais: detectarPaisDelVisitante()
  });

  const [errors, setErrors] = useState({});

  // Corrección propuesta cuando el dominio del email parece un typo
  // ("@gmail.con"). Vacío mientras no haya nada que sugerir.
  const [emailSugerido, setEmailSugerido] = useState('');

  const handleChange = (field) => (event) => {
    const valor = event.target.value;
    // Número escrito o pegado con código ("+591 71234567"): el país se elige
    // solo y en el campo queda el número local.
    if (field === 'whatsapp') {
      const separado = separarPrefijo(valor, formData.pais);
      if (separado) {
        setFormData({ ...formData, pais: separado.pais, whatsapp: separado.numero });
        if (errors.whatsapp) setErrors({ ...errors, whatsapp: '' });
        return;
      }
    }
    setFormData({ ...formData, [field]: valor });
    // Limpiar error cuando el usuario empiece a escribir
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
    // La sugerencia era sobre el email anterior: si lo está reescribiendo, ya
    // no aplica.
    if (field === 'email') {
      setEmailSugerido('');
    }
  };

  /** Reemplaza el email por la corrección propuesta. */
  const aplicarEmailSugerido = () => {
    setFormData({ ...formData, email: emailSugerido });
    setEmailSugerido('');
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.nombre.trim()) {
      newErrors.nombre = 'El nombre es requerido';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'El email es requerido';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }

    if (!formData.instagram.trim()) {
      newErrors.instagram = 'El Instagram es requerido';
    }

    if (!formData.whatsapp.trim()) {
      newErrors.whatsapp = 'El WhatsApp es requerido';
    } else if (!/^\d{8,15}$/.test(formData.whatsapp.replace(/[\s\-().]/g, ''))) {
      newErrors.whatsapp = 'Número inválido (8-15 dígitos)';
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return false;

    // Dominio que parece mal tipeado ("@gmail.con"): se muestra la corrección
    // y se frena el envío UNA vez. Si el lead insiste con el mismo correo,
    // pasa igual — el heurístico puede equivocarse con un dominio raro pero
    // real, y trabar a alguien en el formulario cuesta más que el typo.
    const sugerencia = corregirDominioTipeado(formData.email);
    if (sugerencia && sugerencia !== emailSugerido) {
      setEmailSugerido(sugerencia);
      return false;
    }

    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      const fullWhatsapp = `${prefijoDePais(formData.pais)}${formData.whatsapp.replace(/[\s\-().]/g, '')}`;
      onSubmit({
        nombre: formData.nombre,
        email: formData.email,
        instagram: formData.instagram,
        whatsapp: fullWhatsapp
      });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        // Prevenir cerrar clickeando fuera
        if (reason === 'backdropClick') {
          return;
        }
        onClose();
      }}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          p: 2
        }
      }}
    >
      <Box sx={{ position: 'relative' }}>
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: -8,
            top: -8,
            color: 'grey.500'
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 700 }}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        </Box>

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Nombre Completo"
            variant="outlined"
            value={formData.nombre}
            onChange={handleChange('nombre')}
            error={!!errors.nombre}
            helperText={errors.nombre}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Email"
            variant="outlined"
            type="email"
            value={formData.email}
            onChange={handleChange('email')}
            error={!!errors.email}
            helperText={errors.email}
            sx={{ mb: emailSugerido ? 1 : 2 }}
          />

          {emailSugerido && (
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              ¿Quisiste decir{' '}
              <Box
                component="span"
                onClick={aplicarEmailSugerido}
                sx={{
                  color: 'primary.main',
                  fontWeight: 600,
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
              >
                {emailSugerido}
              </Box>
              ? Si tu correo está bien, vuelve a apretar el botón.
            </Typography>
          )}

          <TextField
            fullWidth
            label="Instagram (usuario)"
            variant="outlined"
            placeholder="@tuusuario"
            value={formData.instagram}
            onChange={handleChange('instagram')}
            error={!!errors.instagram}
            helperText={errors.instagram}
            sx={{ mb: 2 }}
          />

          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Autocomplete
              value={OPCIONES_PAIS.find((p) => p.id === paisPorId(formData.pais).id)}
              onChange={(event, pais) => {
                if (pais) setFormData({ ...formData, pais: pais.id });
              }}
              options={OPCIONES_PAIS}
              groupBy={(pais) => pais.grupo}
              getOptionLabel={textoDePais}
              isOptionEqualToValue={(opcion, valor) =>
                opcion.id === valor.id && opcion.grupo === valor.grupo
              }
              filterOptions={filtrarPaises}
              renderOption={(props, pais) => (
                <li {...props} key={`${pais.grupo}-${pais.id}`}>
                  <Box component="span" sx={{ mr: 1, fontSize: '1.2rem' }}>
                    {banderaDePais(pais.id)}
                  </Box>
                  {pais.country}
                  <Box component="span" sx={{ ml: 'auto', pl: 1, color: 'text.secondary' }}>
                    {pais.code}
                  </Box>
                </li>
              )}
              noOptionsText="No encontramos ese país"
              disableClearable
              autoHighlight
              openOnFocus
              selectOnFocus
              sx={{ width: 122, flexShrink: 0 }}
              slotProps={{
                // La lista se abre más ancha que el campo para que se lean
                // los nombres completos.
                popper: { style: { width: 'min(300px, calc(100vw - 32px))' }, placement: 'bottom-start' },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="País"
                  placeholder="Busca tu país"
                  inputProps={{ ...params.inputProps, 'aria-label': 'País' }}
                />
              )}
            />

            <TextField
              fullWidth
              label="WhatsApp"
              variant="outlined"
              type="tel"
              placeholder="912345678"
              value={formData.whatsapp}
              onChange={handleChange('whatsapp')}
              error={!!errors.whatsapp}
              helperText={errors.whatsapp}
            />
          </Box>

          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            size="large"
            sx={{
              py: 1.5,
              fontSize: '1rem',
              fontWeight: 600,
              mt: 2
            }}
          >
            {submitLabel}
          </Button>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, textAlign: 'center' }}>
            Tus datos están seguros y no serán compartidos
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default VideoAccessForm;
